/**
 * Touch joystick math — pure functions (leaf module, zero imports).
 *
 * Kept separate from the React component so the projection (deadzone, clamp,
 * sign convention) is unit-testable without a DOM.
 */
import type { TouchMoveIntent } from './types';

/** Default pad diameter (CSS px) sized for a landscape phone thumb. */
export const JOYSTICK_DEFAULT_DIAMETER_PX = 144;

/** Displacement below this fraction of the radius reads as centered. */
export const JOYSTICK_DEFAULT_DEADZONE = 0.15;

export interface MoveVector {
  readonly x: number;
  readonly y: number;
}

export interface MoveVectorInput {
  readonly centerX: number;
  readonly centerY: number;
  readonly radius: number;
  readonly pointX: number;
  readonly pointY: number;
  readonly deadzone?: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Projects a pointer position onto the pad's normalized movement vector.
 *
 * Screen space: x right-positive, y down-positive (see `types.ts`). The
 * magnitude is clamped to 1, and a displacement inside `deadzone` of the
 * radius returns the neutral vector so a resting thumb never re-triggers
 * movement.
 */
export function computeMoveVector(input: MoveVectorInput): MoveVector {
  const deadzone = input.deadzone ?? JOYSTICK_DEFAULT_DEADZONE;
  const radius = input.radius > 0 ? input.radius : 1;
  const dx = input.pointX - input.centerX;
  const dy = input.pointY - input.centerY;
  const distance = Math.hypot(dx, dy);
  const normalizedDistance = distance / radius;

  if (distance === 0 || normalizedDistance <= deadzone) {
    return { x: 0, y: 0 };
  }

  const scale = Math.min(normalizedDistance, 1) / distance;
  return {
    x: clamp(dx * scale, -1, 1),
    y: clamp(dy * scale, -1, 1),
  };
}

/** Formats a vector for the prototype readout and stub logs. */
export function formatMoveIntent(intent: TouchMoveIntent): string {
  return `x${intent.x >= 0 ? '+' : ''}${intent.x.toFixed(2)} y${intent.y >= 0 ? '+' : ''}${intent.y.toFixed(2)}`;
}
